// src/routes/AppRoutes.tsx
import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import App from "../pages/App";
import NotFound from "../pages/NotFound";
import Loading from "../components/componets/animations/Loading";
import { useUserStore } from "../stores/userStore";
import { demoMode } from "../config/config";
import CategoryRedirectHandler from "./CategoryRedirectHandler";
import { categoryTabs } from "../config/categoryTabsCofig";
import { EventTimeline } from "../project_athena/components/EventTimeline";

const componentMap: Record<string, React.LazyExoticComponent<any>> = {
    Catalogue: React.lazy(() => import("../pages/views/Catalogue")),
    Landing: React.lazy(() => import("../pages/views/Landing")),
    SmartTraffic: React.lazy(() => import("../pages/SmartTraffic")),
    SmartTrafficCondition: React.lazy(() => import("../pages/SmartTrafficCondition")),
    SmartPedestrian: React.lazy(() => import("../pages/SmartPedestrian_v2")),
    SmartEmissions: React.lazy(() => import("../pages/SmartEmission")),
    SmartEmissions_v2: React.lazy(() => import("../pages/SmartEmission_v2")),
    SmartNoise: React.lazy(() => import("../pages/SmartNoise")),
    SmartEnvironment: React.lazy(() => import("../pages/SmartEnvironment")),
    SmartNaturalEvents: React.lazy(() => import("../pages/SmartNaturalEvents")),
    SmartParking: React.lazy(() => import("../pages/SmartParken_v2")),
    SmartIllegalParking: React.lazy(() => import("../pages/SmartIllegalParking")),
    SmartEnergyMonitoring: React.lazy(() => import("../pages/SmartEnergyMonitoring")),
    SmartWasteDisposal: React.lazy(() => import("../pages/SmartWasteDisposal")),
    Parksuchverkehr: React.lazy(() => import("../pages/KeplerParksuchverkehr")),
    Verkehrsaufkommen: React.lazy(() => import("../pages/KeplerVerkehrsaufkommen")),
    Verlustzeiten: React.lazy(() => import("../pages/KeplerVerlustzeiten")),
    QuelleZielBögen: React.lazy(() => import("../pages/KeplerQuelle-Ziel-Bögen")),
    Parkverteilung: React.lazy(() => import("../pages/KeplerParkverteilung")),
    Verkehrsspinne: React.lazy(() => import("../pages/KeplerVerkehrsspinne")),
    Parkdauer: React.lazy(() => import("../pages/KeplerParkdauer")),
    SmartWaterLevel: React.lazy(() => import("../pages/SmartWaterLevel")),
    FloodRisk: React.lazy(() => import("../pages/SmartWaterLevel_v2")),
    FloodRiskManager: React.lazy(() => import("../pages/SmartFloodRiskManager")),
    LevelOfService: React.lazy(() => import("../pages/KeplerLevelOfService")),
    HeatIsland: React.lazy(() => import("../pages/HeatIslandDarmstadt")),
    SmartHeatIsland: React.lazy(() => import("../pages/SmartHeatIsland")),
    SmartAverageSpeed: React.lazy(() => import("../pages/SmartAverageSpeed")),
    SmartParkingSpots: React.lazy(() => import("../pages/SmartParkingSpots_v2")),
    SmartTicketing: React.lazy(() => import("../pages/SmartTicketing")),
    ParkingMeterData: React.lazy(() => import("../pages/SmartParkingMeterData")),
    ParkingMeterStats: React.lazy(() => import("../pages/SmartParkingMeterStats")),
    ParkingViolations: React.lazy(() => import("../pages/SmartParkingViolations")),
    CurrentTrafficSituation: React.lazy(() => import("../pages/SmartCurrentTrafficSituation")),
    TrafficData: React.lazy(() => import("../pages/SmartTrafficData")),
    SmartTrafficFlow: React.lazy(() => import("../pages/SmartTrafficFlow")),
    SmartWaterQuality: React.lazy(() => import("../pages/SmartWaterQuality")),
    SmartPrecipitation: React.lazy(() => import("../pages/SmartPrecipitation")),
    DownloadManager: React.lazy(() => import("../pages/DownloadManager")),
    ForestFireMonitoring: React.lazy(() => import("../pages/kiwa/ForestFireMonitoring")),
    ForestFireVulnerability: React.lazy(() => import("../pages/kiwa/ForestFireVulnerability")),
    WeatherMonitoring: React.lazy(() => import("../pages/kiwa/WeatherMonitoring")),
    WinterService: React.lazy(() => import("../pages/SmartWinterService")),
    WeatherMeteoblueMap: React.lazy(() => import("../pages/mapPages/WeatherMeteoblueMap")),
    ForestHealth: React.lazy(() => import("../pages/mapPages/NDVIMap")),
    ForestHealthManager: React.lazy(() => import("../pages/SmartForestHealth")),
    InterflowingTrafficFlows: React.lazy(() => import("../pages/AgoraFCD")),
    SmartSurfaceWater: React.lazy(() => import("../pages/SmartSurfaceWater")),
    SmartPedestrianOccupancy: React.lazy(() => import("../pages/SmartPedestrianOccupancy")),
    SmartRealTimeTraffic: React.lazy(() => import("../pages/SmartRealTimeTraffic")),
    SmartMotorhomeTraffic: React.lazy(() => import("../pages/SmartMotorhomeTraffic")),
    SmartFeuerwehrzufahrten: React.lazy(() => import("../pages/SmartFeuerwehrzufahrten")),

    ParkingMeterAnalysis: React.lazy(() => import("../pages/SmartParkingMeterAnalysis")),

    ClimateActionTable: React.lazy(() => import("../pages/ClimateActionTable")),
    ClimateActionHeatmap: React.lazy(() => import("../pages/ClimateActionHeatmap")),
    DonutOverview: React.lazy(() => import("../pages/DonutOverview")),
    Questionnaire: React.lazy(() => import("../pages/Questionnaire")),
    QuestionnaireOverview: React.lazy(() => import("../pages/QuestionnaireOverview")),
    SpendingPulse: React.lazy(() => import("../pages/MastercardSpendingPulse")),
    GeoInsights: React.lazy(() => import("../pages/MastercardGeoInsights")),
    RealTimeTraffic: React.lazy(() => import("../pages/RealTimeTraffic")),
    EventManager: React.lazy(() => import("../project_athena/GenericEventsDesktop")),
    EventTimeline: React.lazy(() => import("../project_athena/CompactTimelineDesktop")),
    PromptLibrary: React.lazy(() => import("../pages/PromtLibrary")),
    V2: React.lazy(() => import("../V2/App")),
};

const AppRoutes = () => {
    const { user } = useUserStore(state => state);
    const userRoles: string[] = user?.roles || [];

    // In demo mode, show ALL routes. Otherwise, use roles to pick routes
    const activePages = demoMode
        ? Object.keys(componentMap)
        : userRoles.filter(role => componentMap[role]);

    // Generate tab URL routes from categoryTabs config
    const urlToTabName = (name: string): string => {
        return name
            .toLowerCase()
            .replace(/[äöüß]/g, (match) => {
                const map: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
                return map[match] || match;
            })
            .replace(/[^a-z0-9]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const generateTabRoutes = () => {
        const routes = [];
        for (const tabs of Object.values(categoryTabs)) {
            for (const tab of tabs) {
                const urlPath = urlToTabName(tab.name);
                routes.push(
                    <Route
                        key={urlPath}
                        path={`/${urlPath}`}
                        element={<CategoryRedirectHandler />}
                    />
                );
            }
        }
        return routes;
    };

    return (
        <Routes>
            <Route element={<App />}>
                <Route path="*" element={<NotFound />} />
                <Route
                    index
                    element={
                        activePages.length > 0
                            ? <Navigate to={activePages[0]} replace />
                            : <Navigate to="NotFound" replace />
                    }
                />

                {/* Category redirect routes - one for each tab */}
                {/* Traffic tabs */}
                <Route path="/fliessender-verkehr" element={<CategoryRedirectHandler />} />
                <Route path="/fliessender-verkehr-mit-fcd" element={<CategoryRedirectHandler />} />
                <Route path="/ruhender-verkehr-einzelplatzdetektion" element={<CategoryRedirectHandler />} />
                <Route path="/ruhender-verkehr-einzelplatzdetektion-mit-fcd" element={<CategoryRedirectHandler />} />
                <Route path="/ruhender-verkehr-bilanzierendes-parken" element={<CategoryRedirectHandler />} />
                <Route path="/ruhender-verkehr-bilanzierendes-parken-mit-fcd" element={<CategoryRedirectHandler />} />
                <Route path="/widerrechtliches-parken" element={<CategoryRedirectHandler />} />
                <Route path="/parkscheinautomaten" element={<CategoryRedirectHandler />} />

                {/* Environment tabs */}
                <Route path="/kommunale-muelleimer" element={<CategoryRedirectHandler />} />
                <Route path="/kommunale-bewaesserung" element={<CategoryRedirectHandler />} />
                <Route path="/umwelt" element={<CategoryRedirectHandler />} />
                <Route path="/kommunale-pegel" element={<CategoryRedirectHandler />} />
                <Route path="/kommunale-gewaesserqualitaet" element={<CategoryRedirectHandler />} />
                <Route path="/kommunale-hitzeinseln" element={<CategoryRedirectHandler />} />
                <Route path="/kommunaler-wald" element={<CategoryRedirectHandler />} />
                <Route path="/winterdienst" element={<CategoryRedirectHandler />} />

                {/* Utility tabs */}
                <Route path="/doughnut-management" element={<CategoryRedirectHandler />} />
                <Route path="/wirtschaft" element={<CategoryRedirectHandler />} />
                <Route path="/prompt-bibliothek" element={<CategoryRedirectHandler />} />

                {/* Existing page routes */}
                {activePages.map(key => {
                    const Component = componentMap[key]!;
                    return (
                        <Route
                            key={key}
                            path={key}
                            element={
                                <Suspense fallback={<Loading />}>
                                    <Component />
                                </Suspense>
                            }
                        />
                    );
                })}
            </Route>
        </Routes>
    );
};

export default AppRoutes;